import { RateCard } from '@shared/models/rate-card.model';
import { RateSheetType } from '@shared/models/enums/rate-sheet-type.enum';

/**
 * CR 5205 – contract rate block (vehicle type & size) selection.
 *
 * A FLAG_DOWN contract keys its blocks by vehicle category id in `flagDownRates`; a COMMITMENT contract lists
 * them in `commitmentBlocks` with `vehicleCategory` as the id. Either may contain the special `default` block.
 * The block key used everywhere (ms-quotations, ms-rates, ms-invoice) is that vehicle category id string.
 */
export const RATE_BLOCK_DEFAULT_KEY = 'default';

export const RATE_BLOCK_CHANGE_WARNING =
  'Are you sure you want to change the vehicle type and size? This will have a direct impact on billing and will not be able to reverse.';

export interface RateBlockOption {
  key: string;
  name: string;
  isDefault: boolean;
}

/** Selected block for one load + contract, as sent to the backend. */
export interface RateBlockSelection {
  selectedRateBlockKey: string | null;
  selectedRateBlockName: string | null;
  autoMatchedRateBlockKey: string | null;
}

export function supportsRateBlockSelection(rateCard: RateCard | null | undefined): boolean {
  return rateCard?.type === RateSheetType.FLAG_DOWN || rateCard?.type === RateSheetType.COMMITMENT;
}

function isDefaultKey(key: string, name: string): boolean {
  return (key || '').trim().toLowerCase() === RATE_BLOCK_DEFAULT_KEY || (name || '').trim().toLowerCase() === RATE_BLOCK_DEFAULT_KEY;
}

/** All selectable blocks of a FLAG_DOWN / COMMITMENT contract, the default block last. Empty for other types. */
export function getRateBlockOptions(rateCard: RateCard | null | undefined): RateBlockOption[] {
  if (!supportsRateBlockSelection(rateCard)) {
    return [];
  }
  const options: RateBlockOption[] = [];
  const seen = new Set<string>();
  const add = (key: string, name: string): void => {
    const k = (key || '').trim();
    if (!k || seen.has(k)) {
      return;
    }
    seen.add(k);
    const displayName = (name || '').trim() || k;
    options.push({ key: k, name: displayName, isDefault: isDefaultKey(k, displayName) });
  };

  if (rateCard.type === RateSheetType.FLAG_DOWN) {
    Object.entries(rateCard.flagDownRates || {}).forEach(([key, rate]) => add(key, rate?.vehicleCategoryName));
  } else {
    (rateCard.commitmentBlocks || []).forEach(block => add(block?.vehicleCategory, block?.vehicleCategoryName));
  }

  return [...options.filter(o => !o.isDefault), ...options.filter(o => o.isDefault)];
}

/**
 * Block the system would pick for a load: exact vehicle category id, else the default block, else null
 * (no match – the user has to pick one). Category names are not unique, so they are never used to match;
 * `categoryName` is kept for signature compatibility and is intentionally ignored.
 */
export function resolveAutoRateBlock(
  rateCard: RateCard | null | undefined,
  categoryId?: string | null,
  categoryName?: string | null
): string | null {
  const options = getRateBlockOptions(rateCard);
  if (!options.length) {
    return null;
  }
  const id = (categoryId || '').trim();
  const byId = id ? options.find(o => o.key === id) : undefined;
  if (byId) {
    return byId.key;
  }
  return options.find(o => o.isDefault)?.key ?? null;
}

export function getRateBlockName(rateCard: RateCard | null | undefined, key: string | null | undefined): string | null {
  if (!key) {
    return null;
  }
  return getRateBlockOptions(rateCard).find(o => o.key === key)?.name ?? null;
}

/** Selection payload for a load: the user's choice when made, otherwise the auto-matched block. */
export function buildRateBlockSelection(
  rateCard: RateCard | null | undefined,
  autoKey: string | null,
  selectedKey?: string | null
): RateBlockSelection | null {
  if (!supportsRateBlockSelection(rateCard)) {
    return null;
  }
  const key = selectedKey ?? autoKey ?? null;
  return {
    selectedRateBlockKey: key,
    selectedRateBlockName: getRateBlockName(rateCard, key),
    autoMatchedRateBlockKey: autoKey ?? null
  };
}
